
import { useEffect, useMemo, useState } from "react";
import { useMutation, useQuery } from "react-query";
import { useParams } from "react-router-dom";
import { atom, useRecoilState } from "recoil";
import { axios } from "../lib/request";
import { nubsQueryKey, nubsUri } from "../lib/refresh";
import { useItemQuery } from "../Global.state";
import { useChallengeQuery } from "./Organize.state";
import { createNub, useReorderAfterDelete } from "../common/NubsUpload/NubsUpload.state";
import { acceptedImages } from '../consts/accepted-files'

export const isResettingMediaState = atom({
    key: 'isResettingMedia',
    default: false,
})

export const fetchNubs = async (game_id, task_id) => {
    if (game_id === "new") {
        return { nubs: [] };
    }

    const response = await axios.get(nubsUri(game_id), {
        params: {
            task_id,
        }
    });

    /** 
     * normalize nubs data so that it follows the existing 
     * item query set in the global state:
     * { 
     *   nubs: [{ 
     *     id: 123, 
     *     nubs: [],
     *     assets: { videos: [] },
     *   }] 
     * }
    */
    const { data } = response
    const nubs = [{
        id: task_id,
        nubs: [],
        assets: { videos: [] },
    }]
    data.nubs.forEach(nub => {
        const existingNub = nubs.findIndex(n => n.id === nub.task_id)
        // Create assets object that matches the current task assets' structure
        // so that it can reuse the VideoThumbnail component and the useVideoThumbnail hook
        const isVideoNub = nub.media_uuid  && nub.media_extension.match(/(?:avi|mkv|mov|mp4|webm)$/g)
        const nubVideo = {
            name: nub.media_uuid,
            urls: {
                mp4: nub.media_url,
                png: `https://ss-assets.socialscavenger.com/images/${nub.media_uuid}-00001.png`
            }
        }
        if (existingNub < 0) {
            nubs.push({
                id: nub.task_id,
                nubs: [nub],
                assets: { 
                    videos: [...isVideoNub ? [nubVideo] : []],
                },
            })
        } else {
            const currentNub = nubs[existingNub]
            nubs.splice(
                existingNub,
                1,
                {
                    ...currentNub,
                    nubs: [
                        ...currentNub.nubs,
                        nub,
                    ],
                    assets: {
                        videos: [
                            ...(currentNub.assets?.videos || []),
                            ...isVideoNub ? [nubVideo] : [],
                        ]
                    },
                }
            )
        }
    })
    return { nubs };
};

export const useNubsDataQuery = (game_id, task_id) => {
    const queryKey = nubsQueryKey(game_id, task_id);

    return useQuery(
        queryKey,
        () => fetchNubs(game_id, task_id),
        {
            suspense: false,
        }
    );
};

export const useNubsQuery = (taskId, config = {}) => {
    const { game_id } = useParams();
    // we need placeholder data because the query is not using suspense
    const { data = { nubs: [] }, isFetching } = useNubsDataQuery(game_id, taskId) || {};
    const queryKey = nubsQueryKey(game_id, taskId);
    // collection setter is handled individually from the state mutations
    const setter = () => {};
    return [...useItemQuery(data, taskId, "nubs", queryKey, setter, config), isFetching];
};

export const useExistingChallengeImageQuery = ({ taskId }) => {
    const [{ assets }, setCollection] = useChallengeQuery(taskId)
    const existingPhoto = useMemo(() => {
        const challengeImage = (assets?.photos || []).find(({ name }) => name === 'standard')?.url || ''
        const imageExtensionsReg = new RegExp(`(.*)+\\.(${acceptedImages.map(ext => ext.replace('.', '')).join('|')})$`)
        const challengeImageUuid = challengeImage.split('/').pop().match(imageExtensionsReg)
        const uuid = challengeImageUuid?.[1]
        const extension = challengeImageUuid?.[2]
        return {
            challengeImage,
            challengeImageUuid,
            uuid,
            extension,
        }
    }, [assets?.photos])
    const updateTask = async (imageNub = {}) => {
        await setCollection({
            assets: {
                ...assets,
                photos: [
                    ...assets.photos.filter(p => p.name !== 'standard'),
                    {
                        name: 'standard',
                        url: imageNub.media_url,
                    }
                ]
            },
            photo_media_uuid: imageNub.media_uuid,
            photo_media_extension: imageNub.media_extension,
        })
    }
    return [existingPhoto, updateTask]
}

export const useDeleteNubMutation = ({ useQuery, taskId }) => {
    const { game_id } = useParams()
    const [collection, setCollection] = useQuery({ invalidate: false })
    const [existingImage, updateTask] = useExistingChallengeImageQuery({ taskId })
    const [, setIsResettingMedia] = useRecoilState(isResettingMediaState)
    const reOrder = useReorderAfterDelete({ useQuery, taskId })
    const { mutateAsync } = useMutation(async (nub) => {
        if (existingImage?.uuid === nub.media_uuid) {
            setIsResettingMedia(true)
            await updateTask({
                media_url: '',
                media_uuid: null,
                media_extension: null,
            })
        }
        await setCollection({
            ...collection,
            nubs: [...collection.nubs].filter(
                n => parseInt(n.id) !== parseInt(nub.id)
            ),
        })
        const url = `${process.env.REACT_APP_API_NUBS_UPDATE_URL}/${nub.id}.json?game_id=${game_id}`;
        await axios.delete(url);
        await reOrder(nub)
        setIsResettingMedia(false)
    });
    return mutateAsync
}

export const useBackwardCompatibleNubs = ({ taskId }) => {
    const { game_id } = useParams()
    const [collection, setCollection] = useNubsQuery(taskId)
    const [{
        challengeImage,
        challengeImageUuid,
        uuid,
        extension,
    }] = useExistingChallengeImageQuery({ taskId })
    const [uploadingNub, setUploadingNub] = useState(false)
    useEffect(() => {
        /**
         * if there is an existing challenge image, and no nubs, 
         * create a first nub with the original challenge image as migration.
         */
        let synced = false
        if (!synced && !uploadingNub && challengeImageUuid && collection?.nubs?.length === 0) {            const uploadAsNub = async () => {
                setUploadingNub(true)
                try {
                    const newNub = {
                        position: collection.nubs.length + 1,
                        [`media_uuid`]: uuid,
                        [`media_extension`]: extension.toLocaleLowerCase(),
                        [`media_url`]: challengeImage,
                    }
                    const created = await createNub(game_id, {
                        ...newNub,
                        task_id: taskId,
                    })
                    if (created?.data?.nub) {
                        await setCollection({
                            ...collection,
                            nubs: [
                                ...collection.nubs,
                                {
                                    ...created.data.nub,
                                    ...newNub,
                                },
                            ],
                        });
                    }
                } finally {
                    setUploadingNub(false)
                }
            }
            uploadAsNub()
        }
        return () => {
            synced = true
        }
    }, [
        uploadingNub,
        collection,
        setCollection,
        challengeImage,
        challengeImageUuid,
        uuid,
        extension,
        game_id,
        taskId,
    ])
}

export const useSyncNubsWithChallengeImage = ({ taskId }) => {
    const [collection] = useNubsQuery(taskId)
    const [{ assets }, updateTask] = useChallengeQuery(taskId)
    const [isResettingMedia, setIsResettingMedia] = useRecoilState(isResettingMediaState)

    useEffect(() => {
        /**
         * if there is no existing challenge image, 
         * then the last image uploaded to carousel should be set at the original challenge image 
         * for backwards compatibility with player apps.
         */
        let synced = false

        const syncImageNubs = async () => {
            // current challenge image
            const challengeImage = (assets?.photos || []).find(({ name }) => name === 'standard')?.url

            // accepted image extensions
            const imageExtensionsReg = new RegExp(acceptedImages.map(ext => ext.replace('.', '')).join('|'))

            // current image nubs
            const imageNubs = (collection?.nubs || []).filter(n => n?.media_extension?.match(imageExtensionsReg)).map(
                (nub) => {
                    // Get the nub media URL
                    let mediaUrl = nub.media_url

                    // only use the media_url that has been resolved from the server
                    // and not using local media URL (base64)
                    if (nub.media_url?.match(/^data:image/)) {
                        mediaUrl = nub.media_url_raw
                    }

                    return {
                        ...nub,
                        mediaUrl,
                    }
                }
            )


            // Get the last image nub
            const imageNub = [...imageNubs].pop()

            if (
                // Execute when the effect is not run yet
                !synced && 
                // And the app is not resetting media
                !isResettingMedia && 
                // And it has image nubs
                imageNubs.length > 0 && 
                // And the current challenge image is not the last nub's media URL
                imageNub?.mediaUrl !== challengeImage
            ) {
                const { mediaUrl, media_uuid, media_extension } = imageNub

                // Flag this process as true to avoid infinite loop
                setIsResettingMedia(true)
    
                await updateTask({
                    assets: {
                        ...assets,
                        photos: [
                            ...assets.photos.filter(p => p.name !== 'standard'),
                            {
                                name: 'standard',
                                url: mediaUrl,
                            }
                        ]
                    },
                    photo_media_uuid: media_uuid,
                    photo_media_extension: media_extension,
                })

                setIsResettingMedia(false)
            }
        }

        syncImageNubs()

        return () => {
            synced = true
        }
    }, [
        assets,
        collection?.nubs,
        updateTask,
        isResettingMedia,
        setIsResettingMedia,
    ])
}